/**
 * "Recomendados" in Configurações › Skills e MCPs (docs/refactor/42): a short, curated list from the
 * server — famous, trusted sources only — so starting well takes one click instead of a hunt.
 *
 * Skills install through the path that already existed: the person's own AI fetches the repository
 * (catalog prompt `skills.install-from-repo`) and the machine's on/off switch stays the way to
 * disable one. MCPs connect through the desktop's add box: a verified configuration goes straight
 * in; one that needs a secret lands in the box with the ${VALUE} left to fill; one whose project
 * has no stable published command goes to the person's AI to write from the repository.
 */

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Download, ExternalLink, Plug, Sparkles } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { localized, usePromptCatalog } from '../lib/prompts'
import { fillUrl, unfilledReferences } from '../lib/skillsAndMcps'
import type { RecommendedMcp } from '../lib/types'
import { Badge, Panel } from './primitives'

function useRecommended() {
  return useQuery({ queryKey: ['recommended'], queryFn: api.recommended, staleTime: 10 * 60_000 })
}

/** The "explore more" line: the official MCP registry and the awesome lists, for whoever wants beyond the short list. */
function MoreLinks() {
  const { t } = useI18n()
  const { data } = useRecommended()
  if (!data || data.more.length === 0) return null
  return (
    <span className="tiny muted">
      {t('recommended.more')}{' '}
      {data.more.map((link, i) => (
        <span key={link.url}>
          {i > 0 && ' · '}
          <a href={link.url} target="_blank" rel="noreferrer">{link.name}</a>
        </span>
      ))}
    </span>
  )
}

/** The curated skill sources, with one-click install through the person's own AI. Web and desktop. */
export function RecommendedSkills() {
  const { t, locale } = useI18n()
  const { data } = useRecommended()
  const { data: catalog } = usePromptCatalog()
  const [sent, setSent] = useState<Record<string, boolean>>({})
  const [problem, setProblem] = useState<string | null>(null)

  const install = useMutation({
    mutationFn: async (repo: string) => {
      const prompt = catalog?.prompts.find((p) => p.key === 'skills.install-from-repo')
      if (!prompt) throw new Error('catalog')
      return api.workOrders.create({
        kind: 'Once',
        instructions: fillUrl(prompt.body, repo),
        title: `${localized(prompt.title, locale)}: ${repo}`.slice(0, 200),
        repositoryId: null,
      })
    },
    onSuccess: (_created, repo) => {
      setSent((all) => ({ ...all, [repo]: true }))
      setProblem(null)
    },
    onError: (e) => setProblem(e instanceof Error && e.message === 'catalog' ? t('skillsMachine.noCatalog') : t('error.generic')),
  })

  if (!data || data.skills.length === 0) return null
  return (
    <Panel title={<span className="row row--tight"><Sparkles size={15} aria-hidden /> {t('recommended.skillsTitle')}</span>}>
      <div className="stack">
        <span className="small muted">{t('recommended.skillsIntro')}</span>
        {data.skills.map((skill) => (
          <div key={skill.key} className="row row--between row--wrap" style={{ alignItems: 'flex-start', gap: 12 }}>
            <div className="stack stack--tight" style={{ flex: 1, minWidth: 240 }}>
              <strong className="small">{skill.name} <a className="tiny muted" href={skill.repo} target="_blank" rel="noreferrer"><ExternalLink size={11} aria-hidden /> {t('recommended.source')}</a></strong>
              <span className="tiny muted">{localized(skill.description, locale)}</span>
            </div>
            {sent[skill.repo] ? (
              <span className="tiny muted">{t('recommended.sent')}</span>
            ) : (
              <button type="button" className="btn btn--sm" onClick={() => install.mutate(skill.repo)} disabled={install.isPending}>
                <Download size={13} /> {t('recommended.install')}
              </button>
            )}
          </div>
        ))}
        {problem && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{problem}</span>}
        <MoreLinks />
      </div>
    </Panel>
  )
}

/** What pressing "Conectar" on an entry should do, decided by what the catalog knows about it. */
export function mcpConnectPlan(mcp: RecommendedMcp): { kind: 'add'; config: string } | { kind: 'fill'; config: string } | { kind: 'repo'; repo: string } {
  if (!mcp.config) return { kind: 'repo', repo: mcp.repo }
  return { kind: unfilledReferences(mcp.config).length > 0 ? 'fill' : 'add', config: mcp.config }
}

/**
 * The curated MCP servers. On the desktop the handlers connect for real (straight add, fill-in box,
 * or the person's AI); on the web the list still informs, and the button says where connecting lives.
 */
export function RecommendedMcps({ onConnect }: { onConnect?: (mcp: RecommendedMcp) => void }) {
  const { t, locale } = useI18n()
  const { data } = useRecommended()

  if (!data || data.mcps.length === 0) return null
  return (
    <Panel title={<span className="row row--tight"><Plug size={15} aria-hidden /> {t('recommended.mcpsTitle')}</span>}>
      <div className="stack">
        <span className="small muted">{t('recommended.mcpsIntro')}</span>
        {data.mcps.map((mcp) => (
          <div key={mcp.key} className="row row--between row--wrap" style={{ alignItems: 'flex-start', gap: 12 }}>
            <div className="stack stack--tight" style={{ flex: 1, minWidth: 240 }}>
              <strong className="small">
                {mcp.name} <a className="tiny muted" href={mcp.repo} target="_blank" rel="noreferrer"><ExternalLink size={11} aria-hidden /> {t('recommended.source')}</a>
                {mcp.requires && <Badge tone="neutral">{t('recommended.needsValue', { value: mcp.requires })}</Badge>}
              </strong>
              <span className="tiny muted">{localized(mcp.description, locale)}</span>
            </div>
            {onConnect ? (
              <button type="button" className="btn btn--sm" onClick={() => onConnect(mcp)}>
                <Plug size={13} /> {mcp.config ? t('recommended.connect') : t('recommended.viaAi')}
              </button>
            ) : (
              <span className="tiny muted">{t('recommended.needsDesktop')}</span>
            )}
          </div>
        ))}
        <MoreLinks />
      </div>
    </Panel>
  )
}
